import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import PlayCircleFilledRoundedIcon from "@mui/icons-material/PlayCircleFilledRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import { formatCurrency, formatDate } from "./constants";
import { getProductStockQuantity } from "../../utils/productFilters";
import { updateProduct, getAuthenticityOptions } from "../../api/productService";
import { getCategoryAttributes } from "../../api/categoryService";
import { reviewProduct } from "../../api/adminService";
import { translateToArabicAndHebrew } from "../../api/translationService";

const APPROVAL_APPROVED = 1;
const APPROVAL_PENDING = 2;
const APPROVAL_REJECTED = 3;

const DISCOUNT_NONE = 0;
const DISCOUNT_FIXED = 1;
const DISCOUNT_PERCENT = 2;

const ATTRIBUTE_INPUT_DROPDOWN = 1;
const ATTRIBUTE_INPUT_NUMBER = 3;

const VIDEO_PLACEMENT_LABELS = {
  1: "Video shown first",
  2: "Main photo, then video",
  3: "Photos first, video last",
};

// Fields look like plain text on a soft grey fill and turn into a normal input on focus,
// so the page reads like a details view but every value can be edited in place.
const editSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "action.hover",
    transition: "background-color .15s",
    "&:hover": { bgcolor: "action.selected" },
    "&.Mui-focused": { bgcolor: "background.paper" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "& .MuiOutlinedInput-root:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
};

function discountPercentOf(price, salePrice) {
  if (salePrice == null || !(price > 0) || salePrice >= price) return null;
  return Math.round(((price - salePrice) / price) * 100);
}

function isFuture(value) {
  return value && new Date(value) > new Date();
}

function scheduleText(item) {
  const parts = [];
  if (isFuture(item.discountStartsAt)) parts.push(`starts ${formatDate(item.discountStartsAt)}`);
  if (item.discountEndsAt) parts.push(`${isFuture(item.discountEndsAt) ? "ends" : "ended"} ${formatDate(item.discountEndsAt)}`);
  return parts.join(" · ");
}

function formFromProduct(p) {
  const attributeValues = {};
  (p.attributeValues || []).forEach((av) => {
    attributeValues[av.attributeId] = { optionId: av.optionId ?? null, textValue: av.textValue ?? "" };
  });
  const discountPrice = p.discountPrice ?? p.scheduledDiscountPrice;
  return {
    name: p.name ?? "",
    nameArabic: p.nameArabic ?? "",
    nameHebrew: p.nameHebrew ?? "",
    description: p.description ?? "",
    descriptionArabic: p.descriptionArabic ?? "",
    descriptionHebrew: p.descriptionHebrew ?? "",
    price: p.price != null ? String(p.price) : "",
    wholesalePrice: p.wholesalePrice != null ? String(p.wholesalePrice) : "",
    discountType: p.discountType ?? DISCOUNT_NONE,
    discountPrice: discountPrice != null ? String(discountPrice) : "",
    discountPercent: p.discountPercent != null ? String(p.discountPercent) : "",
    stockQuantity: String(p.stockQuantity ?? 0),
    sku: p.sku ?? "",
    isActive: p.isActive ?? true,
    requiresSpecialDelivery: Boolean(p.requiresSpecialDelivery),
    authenticityOptionId: p.authenticityOptionId ?? null,
    attributeValues,
  };
}

// Comparable snapshot of a form: empty attribute entries are dropped so clearing a value that was
// never set doesn't count as a change.
function snapshot(form) {
  const attrs = {};
  Object.keys(form.attributeValues)
    .sort()
    .forEach((id) => {
      const v = form.attributeValues[id];
      if (v.optionId != null) attrs[id] = `o${v.optionId}`;
      else if ((v.textValue || "").trim()) attrs[id] = `t${v.textValue.trim()}`;
    });
  return JSON.stringify({ ...form, attributeValues: attrs });
}

function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2.5, height: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.75, minHeight: 32 }}>
        {Icon && <Icon fontSize="small" color="action" />}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.disabled">
          {hint}
        </Typography>
      )}
    </Stack>
  );
}

function InfoRow({ label, children }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ py: 1, alignItems: "baseline", borderBottom: "1px dashed", borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ width: 120, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, minWidth: 0, typography: "body2", fontWeight: 500 }}>
        {children ?? <Typography component="span" variant="body2" color="text.disabled">—</Typography>}
      </Box>
    </Stack>
  );
}

function StatTile({ icon: Icon, label, value, color = "primary", hint }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.07),
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette[color].main, 0.18),
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", color: `${color}.main`, mb: 0.25 }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

function SwitchRow({ title, hint, checked, onChange, color = "primary" }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", px: 1.5, py: 0.75, borderRadius: 2, bgcolor: "action.hover", cursor: "pointer" }}
      onClick={() => onChange(!checked)}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
      <Switch color={color} checked={checked} onClick={(e) => e.stopPropagation()} onChange={(e) => onChange(e.target.checked)} />
    </Stack>
  );
}

function ProductGallery({ product, onManage }) {
  const images = useMemo(
    () => [...(product.images || [])].sort((a, b) => b.isMain - a.isMain || a.displayOrder - b.displayOrder),
    [product.images],
  );
  const media = useMemo(() => {
    const list = images.map((img) => ({ key: `img-${img.imageId}`, type: "image", url: img.imageUrl, isMain: img.isMain }));
    if (product.videoUrl) list.push({ key: "video", type: "video", url: product.videoUrl });
    return list;
  }, [images, product.videoUrl]);
  const [selectedKey, setSelectedKey] = useState(null);
  const selected = media.find((m) => m.key === selectedKey) || media[0];

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          aspectRatio: "1 / 1",
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: selected?.type === "video" ? "#000" : "background.paper",
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!selected ? (
          <Stack sx={{ alignItems: "center", color: "text.disabled" }}>
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 48 }} />
            <Typography variant="body2">No photos yet</Typography>
          </Stack>
        ) : selected.type === "video" ? (
          <Box component="video" src={selected.url} controls sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <>
            <Box component="img" src={selected.url} alt={product.name} sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
            <Tooltip title="Open full size">
              <IconButton
                component="a"
                href={selected.url}
                target="_blank"
                rel="noreferrer"
                size="small"
                sx={{ position: "absolute", top: 10, right: 10, bgcolor: "rgba(0,0,0,0.5)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
              >
                <OpenInNewRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {selected?.isMain && (
          <Chip size="small" color="primary" label="Main photo" sx={{ position: "absolute", top: 10, left: 10, fontWeight: 700 }} />
        )}
      </Box>

      {media.length > 1 && (
        <Box sx={{ display: "flex", gap: 1, mt: 1.25, overflowX: "auto", pb: 0.5 }}>
          {media.map((m) => (
            <Box
              key={m.key}
              onClick={() => setSelectedKey(m.key)}
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: 1.5,
                overflow: "hidden",
                cursor: "pointer",
                bgcolor: m.type === "video" ? "#000" : "background.paper",
                border: "2px solid",
                borderColor: m.key === selected?.key ? "primary.main" : "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {m.type === "video" ? (
                <PlayCircleFilledRoundedIcon sx={{ color: "#fff" }} />
              ) : (
                <Box component="img" src={m.url} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
              )}
            </Box>
          ))}
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 1.25, alignItems: "center" }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {images.length} photo{images.length === 1 ? "" : "s"}
          {product.videoUrl ? ` · video (${VIDEO_PLACEMENT_LABELS[product.videoPlacement] || "default order"})` : " · no video"}
        </Typography>
        <Button size="small" startIcon={<PhotoLibraryOutlinedIcon />} onClick={onManage}>
          Manage photos
        </Button>
      </Stack>
    </Box>
  );
}

function VariantsList({ variants }) {
  if (variants.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No variants - this product is sold as a single item.
      </Typography>
    );
  }
  return (
    <Stack divider={<Divider flexItem />}>
      {variants.map((v) => {
        const salePrice = v.discountPrice != null && v.discountPrice < v.price ? v.discountPrice : null;
        const percent = discountPercentOf(v.price, salePrice);
        const schedule = scheduleText(v);
        return (
          <Stack key={v.variantId} direction="row" spacing={1.5} sx={{ py: 1.25, alignItems: "center", opacity: v.isActive ? 1 : 0.6 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {v.imageUrl ? (
                <Box component="img" src={v.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <ImageNotSupportedOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              )}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {v.variantName || "Unnamed variant"}
                </Typography>
                {!v.isActive && <Chip size="small" variant="outlined" label="Inactive" sx={{ height: 20, fontSize: 11 }} />}
              </Stack>
              {v.attributeValues?.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {v.attributeValues.map((av) => (
                    <Chip
                      key={av.attributeId}
                      size="small"
                      variant="outlined"
                      label={`${av.attributeName}: ${av.optionValue}${av.unit ? ` ${av.unit}` : ""}`}
                      avatar={
                        av.optionColorHex ? (
                          <Box sx={{ bgcolor: av.optionColorHex, borderRadius: "50%", border: "1px solid", borderColor: "divider" }} />
                        ) : undefined
                      }
                      sx={{ height: 22, fontSize: 11.5 }}
                    />
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                SKU: {v.sku || "—"}
                {schedule ? ` · sale ${schedule}` : ""}
                {v.discountPrice == null && v.scheduledDiscountPrice != null ? ` · scheduled sale ${formatCurrency(v.scheduledDiscountPrice)}` : ""}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: salePrice != null ? "error.main" : "text.primary" }}>
                {formatCurrency(salePrice ?? v.price)}
                {percent != null && (
                  <Box component="span" sx={{ ml: 0.5, fontSize: 11 }}>
                    −{percent}%
                  </Box>
                )}
              </Typography>
              {salePrice != null && (
                <Typography variant="caption" color="text.secondary" sx={{ textDecoration: "line-through", display: "block" }}>
                  {formatCurrency(v.price)}
                </Typography>
              )}
              <Typography variant="caption" sx={{ fontWeight: 600, color: v.stockQuantity > 0 ? "success.main" : "error.main", display: "block" }}>
                {v.stockQuantity > 0 ? `${v.stockQuantity} in stock` : "Out of stock"}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

// Admin product page: looks like a clean details view, but every text/number/toggle field is
// editable in place and saved with one button. Photos, video, variants and category keep their
// dedicated tools in the full editor (onOpenFullEditor).
export default function ProductDetailsDialog({
  product,
  store,
  storeTypeLabel,
  busy: externalBusy,
  externalError,
  onClose,
  onOpenFullEditor,
  onSaved,
  onApproved,
  onReject,
}) {
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [base, setBase] = useState(product);
  const [form, setForm] = useState(() => formFromProduct(product));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(null);

  const [authenticityOptions, setAuthenticityOptions] = useState([]);
  const [categoryAttributes, setCategoryAttributes] = useState(null); // null = loading, [] = none
  const [attributesFailed, setAttributesFailed] = useState(false);

  const busy = saving || externalBusy;
  const supportsWholesale = store ? store.type === 2 || store.type === 3 : false;
  const dirty = snapshot(form) !== snapshot(formFromProduct(base));

  useEffect(() => {
    let active = true;
    getAuthenticityOptions()
      .then((data) => active && setAuthenticityOptions(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setCategoryAttributes(null);
    setAttributesFailed(false);
    getCategoryAttributes(base.categoryId, base.storeId ?? store?.storeId)
      .then((data) => {
        if (active) setCategoryAttributes(data.filter((a) => !a.isVariant).sort((a, b) => a.displayOrder - b.displayOrder));
      })
      .catch(() => {
        if (!active) return;
        setAttributesFailed(true);
        setCategoryAttributes([]);
      });
    return () => {
      active = false;
    };
  }, [base.categoryId, base.storeId, store?.storeId]);

  const set = (field) => (value) => {
    setSavedFlash(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const setAttr = (attributeId, value) => {
    setSavedFlash(false);
    setForm((prev) => ({ ...prev, attributeValues: { ...prev.attributeValues, [attributeId]: value } }));
  };

  // Live price preview from the form.
  const priceNum = parseFloat(form.price);
  let previewSale = null;
  if (priceNum > 0 && form.discountType === DISCOUNT_FIXED) {
    const v = parseFloat(form.discountPrice);
    if (v > 0 && v < priceNum) previewSale = v;
  } else if (priceNum > 0 && form.discountType === DISCOUNT_PERCENT) {
    const p = parseFloat(form.discountPercent);
    if (p > 0 && p < 100) previewSale = Math.round(priceNum * (1 - p / 100) * 100) / 100;
  }
  const previewPercent = discountPercentOf(priceNum, previewSale);
  const discountScheduled = !dirty && base.discountPrice == null && base.scheduledDiscountPrice != null;
  const discountSchedule = scheduleText(base);

  const variants = base.variants || [];
  const activeVariants = variants.filter((v) => v.isActive);
  const totalStock = getProductStockQuantity(base);
  const rating = Number(base.rating || 0);

  async function handleTranslate() {
    const sources = [
      ["name", form.name],
      ["description", form.description],
    ].filter(([, text]) => text.trim());
    if (sources.length === 0) return;
    setTranslating(true);
    setError("");
    try {
      const results = await Promise.all(sources.map(([, text]) => translateToArabicAndHebrew(text)));
      setForm((prev) => {
        const next = { ...prev };
        sources.forEach(([field], i) => {
          next[`${field}Arabic`] = results[i].arabic;
          next[`${field}Hebrew`] = results[i].hebrew;
        });
        return next;
      });
    } catch {
      setError("Auto-translate failed. Please try again.");
    } finally {
      setTranslating(false);
    }
  }

  function buildPayload() {
    const name = form.name.trim();
    const price = parseFloat(form.price);
    if (!name) return { error: "Please enter a product name." };
    if (Number.isNaN(price) || price < 0) return { error: "Please enter a valid price." };

    let wholesalePrice = null;
    if (form.wholesalePrice.trim() !== "") {
      wholesalePrice = parseFloat(form.wholesalePrice);
      if (Number.isNaN(wholesalePrice) || wholesalePrice < 0) return { error: "Please enter a valid wholesale price." };
    }

    const stock = parseInt(form.stockQuantity, 10);
    if (Number.isNaN(stock) || stock < 0) return { error: "Stock must be 0 or more." };

    let discountPrice = null;
    let discountPercent = null;
    if (form.discountType === DISCOUNT_FIXED) {
      discountPrice = parseFloat(form.discountPrice);
      if (Number.isNaN(discountPrice) || discountPrice <= 0 || discountPrice >= price) {
        return { error: "Sale price must be more than 0 and less than the regular price." };
      }
    } else if (form.discountType === DISCOUNT_PERCENT) {
      discountPercent = parseFloat(form.discountPercent);
      if (Number.isNaN(discountPercent) || discountPercent <= 0 || discountPercent >= 100) {
        return { error: "Discount must be between 1 and 99%." };
      }
    }

    let attributeValues;
    if (attributesFailed || !categoryAttributes) {
      // Attribute list didn't load - resend what the product already has so nothing is lost.
      attributeValues = (base.attributeValues || []).map((av) =>
        av.optionId != null ? { attributeId: av.attributeId, optionId: av.optionId } : { attributeId: av.attributeId, textValue: av.textValue },
      );
    } else {
      attributeValues = categoryAttributes
        .map((attr) => {
          const v = form.attributeValues[attr.attributeId];
          if (!v) return null;
          if (attr.inputType === ATTRIBUTE_INPUT_DROPDOWN) {
            return v.optionId != null ? { attributeId: attr.attributeId, optionId: v.optionId } : null;
          }
          const text = (v.textValue || "").trim();
          return text ? { attributeId: attr.attributeId, textValue: text } : null;
        })
        .filter(Boolean);
      const missing = categoryAttributes.filter((a) => a.isRequired && !attributeValues.some((v) => v.attributeId === a.attributeId));
      if (missing.length > 0) return { error: `Please fill in: ${missing.map((a) => a.attributeName).join(", ")}.` };
    }

    return {
      payload: {
        categoryId: base.categoryId,
        name,
        description: form.description.trim() || null,
        nameArabic: form.nameArabic.trim(),
        nameHebrew: form.nameHebrew.trim(),
        descriptionArabic: form.descriptionArabic.trim(),
        descriptionHebrew: form.descriptionHebrew.trim(),
        price,
        wholesalePrice,
        discountType: form.discountType,
        discountPrice,
        discountPercent,
        stockQuantity: stock,
        sku: form.sku.trim() || null,
        isActive: form.isActive,
        videoPlacement: base.videoPlacement,
        authenticityOptionId: form.authenticityOptionId,
        requiresSpecialDelivery: form.requiresSpecialDelivery,
        attributeValues,
      },
    };
  }

  // Saves the form; returns false (and shows the error) if it couldn't.
  async function save() {
    const { payload, error: validationError } = buildPayload();
    if (validationError) {
      setError(validationError);
      return false;
    }
    setError("");
    setSaving(true);
    try {
      await updateProduct(base.productId, payload);
      // Re-read from the list so names of newly chosen options etc. are filled in.
      const fresh = (await onSaved(`"${payload.name}" was saved.`)) || base;
      setBase(fresh);
      setForm(formFromProduct(fresh));
      setSavedFlash(true);
      return true;
    } catch (err) {
      const data = err?.response?.data;
      setError(typeof data === "string" && data ? data : "Could not save the product. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (dirty && !(await save())) return;
    setSaving(true);
    try {
      await reviewProduct(base.productId, true);
      onApproved(`"${form.name.trim() || base.name}" is approved and live.`);
    } catch (err) {
      const data = err?.response?.data;
      setError(typeof data === "string" && data ? data : "Could not approve the product.");
      setSaving(false);
    }
  }

  function guard(action) {
    if (dirty) setConfirmDiscard(() => action);
    else action();
  }

  function handleKeyDown(e) {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      if (dirty && !busy) save();
    }
  }

  const canApprove = base.approvalStatus !== APPROVAL_APPROVED;
  const canReject = base.approvalStatus === APPROVAL_PENDING;
  const shownError = error || externalError;

  const translationColumns = [
    { code: "en", title: "English", dir: "ltr", nameField: null, descField: "description" },
    { code: "ar", title: "العربية · Arabic", dir: "rtl", nameField: "nameArabic", descField: "descriptionArabic" },
    { code: "he", title: "עברית · Hebrew", dir: "rtl", nameField: "nameHebrew", descField: "descriptionHebrew" },
  ];

  return (
    <Dialog
      open
      onClose={busy ? undefined : () => guard(onClose)}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      scroll="paper"
      onKeyDown={handleKeyDown}
      slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : 3, bgcolor: "background.default", backgroundImage: "none" } } }}
    >
      {/* Header */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", px: { xs: 2, sm: 3 }, py: 1.25, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0, lineHeight: 1.6 }} noWrap>
          Product #{base.productId}
          {store?.storeName ? ` · ${store.storeName}` : ""}
          {storeTypeLabel ? ` · ${storeTypeLabel}` : ""}
        </Typography>
        <Tooltip title="Close">
          <span>
            <IconButton onClick={() => guard(onClose)} disabled={busy}>
              <CloseRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={{ xs: 2, md: 2.5 }}>
          {base.approvalStatus === APPROVAL_REJECTED && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              <b>Rejected.</b> {base.approvalRejectionReason ? `Reason: ${base.approvalRejectionReason}` : "No reason was given."}
            </Alert>
          )}
          {base.approvalStatus === APPROVAL_PENDING && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Waiting for review - customers can't see this product until it's approved.
            </Alert>
          )}

          {/* Overview */}
          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Grid container spacing={{ xs: 2.5, md: 4 }}>
              <Grid size={{ xs: 12, md: 5 }}>
                <ProductGallery product={base} onManage={() => guard(() => onOpenFullEditor(base))} />
              </Grid>

              <Grid size={{ xs: 12, md: 7 }}>
                <Stack spacing={2.25}>
                  {/* Category + status */}
                  <Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.75, mb: 1 }}>
                      <Chip size="small" color="primary" variant="outlined" label={base.categoryName || "Uncategorised"} />
                      {base.approvalStatus === APPROVAL_APPROVED && form.isActive && <Chip size="small" color="success" label="Live" />}
                      {base.approvalStatus === APPROVAL_PENDING && <Chip size="small" color="warning" label="Waiting for review" />}
                      {base.approvalStatus === APPROVAL_REJECTED && <Chip size="small" color="error" label="Rejected" />}
                      {!form.isActive && <Chip size="small" variant="outlined" label="Hidden" />}
                      {base.hasActiveCoupon && <Chip size="small" color="secondary" variant="outlined" label="Has active coupon" />}
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={3}
                      placeholder="Product name"
                      value={form.name}
                      onChange={(e) => set("name")(e.target.value)}
                      error={!form.name.trim()}
                      sx={{ ...editSx, "& .MuiInputBase-input": { fontSize: 22, fontWeight: 800, lineHeight: 1.3 } }}
                    />
                  </Box>

                  {/* Price & stock */}
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <FieldLabel>Price</FieldLabel>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={form.price}
                        onChange={(e) => set("price")(e.target.value)}
                        sx={{ ...editSx, "& .MuiInputBase-input": { fontWeight: 700, fontSize: 18 } }}
                        slotProps={{
                          input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                          htmlInput: { min: 0, step: "0.01" },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4 }}>
                      <FieldLabel hint={activeVariants.length > 0 ? "(variants count)" : undefined}>Stock</FieldLabel>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={form.stockQuantity}
                        onChange={(e) => set("stockQuantity")(e.target.value)}
                        sx={{ ...editSx, "& .MuiInputBase-input": { fontWeight: 700, fontSize: 18 } }}
                        slotProps={{ htmlInput: { min: 0, step: 1 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FieldLabel>SKU</FieldLabel>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Not set"
                        value={form.sku}
                        onChange={(e) => set("sku")(e.target.value)}
                        sx={{ ...editSx, "& .MuiInputBase-input": { fontSize: 15, py: "10px" } }}
                      />
                    </Grid>
                    {(supportsWholesale || base.wholesalePrice != null) && (
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FieldLabel>Wholesale price</FieldLabel>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Not set"
                          value={form.wholesalePrice}
                          onChange={(e) => set("wholesalePrice")(e.target.value)}
                          sx={editSx}
                          slotProps={{
                            input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                            htmlInput: { min: 0, step: "0.01" },
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {/* Discount */}
                  <Box>
                    <FieldLabel>Discount</FieldLabel>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        value={form.discountType}
                        onChange={(_, v) => v != null && set("discountType")(v)}
                        sx={{ "& .MuiToggleButton-root": { px: 1.75, textTransform: "none", fontWeight: 600 } }}
                      >
                        <ToggleButton value={DISCOUNT_NONE}>None</ToggleButton>
                        <ToggleButton value={DISCOUNT_FIXED}>Sale price</ToggleButton>
                        <ToggleButton value={DISCOUNT_PERCENT}>Percent</ToggleButton>
                      </ToggleButtonGroup>
                      {form.discountType === DISCOUNT_FIXED && (
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Sale price"
                          value={form.discountPrice}
                          onChange={(e) => set("discountPrice")(e.target.value)}
                          sx={{ ...editSx, flexGrow: 1 }}
                          slotProps={{
                            input: { startAdornment: <InputAdornment position="start">₪</InputAdornment> },
                            htmlInput: { min: 0, step: "0.01" },
                          }}
                        />
                      )}
                      {form.discountType === DISCOUNT_PERCENT && (
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Percent off"
                          value={form.discountPercent}
                          onChange={(e) => set("discountPercent")(e.target.value)}
                          sx={{ ...editSx, flexGrow: 1 }}
                          slotProps={{
                            input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                            htmlInput: { min: 0, max: 100, step: 1 },
                          }}
                        />
                      )}
                    </Stack>
                    {previewSale != null && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          mt: 1.25,
                          px: 1.5,
                          py: 0.9,
                          borderRadius: 2,
                          alignItems: "baseline",
                          flexWrap: "wrap",
                          bgcolor: (theme) => alpha(theme.palette.error.main, 0.06),
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Customers pay
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "error.main" }}>
                          {formatCurrency(previewSale)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                          {formatCurrency(priceNum)}
                        </Typography>
                        {previewPercent != null && <Chip size="small" color="error" label={`−${previewPercent}%`} sx={{ height: 20, fontWeight: 700 }} />}
                        {discountSchedule && (
                          <Typography variant="caption" color="text.secondary" sx={{ width: "100%" }}>
                            {discountScheduled ? "Scheduled sale · " : ""}
                            {discountSchedule}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>

                  {/* Visibility & delivery */}
                  <Grid container spacing={1.25}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <SwitchRow
                        title="Visible to customers"
                        hint={form.isActive ? "Shown in the store" : "Hidden from the store"}
                        checked={form.isActive}
                        onChange={set("isActive")}
                        color="success"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <SwitchRow
                        title="Special delivery"
                        hint={base.deliveryFee != null ? `Delivery fee ${formatCurrency(base.deliveryFee)}` : "Large or fragile items"}
                        checked={form.requiresSpecialDelivery}
                        onChange={set("requiresSpecialDelivery")}
                      />
                    </Grid>
                  </Grid>

                  <Box>
                    <FieldLabel>Authenticity</FieldLabel>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={form.authenticityOptionId ?? ""}
                      onChange={(e) => set("authenticityOptionId")(e.target.value === "" ? null : Number(e.target.value))}
                      sx={editSx}
                      slotProps={{ select: { displayEmpty: true } }}
                    >
                      <MenuItem value="">
                        <Typography component="span" color="text.secondary">
                          Not specified
                        </Typography>
                      </MenuItem>
                      {/* Keep the current value selectable even before the option list loads. */}
                      {base.authenticityOptionId != null &&
                        !authenticityOptions.some((o) => o.authenticityOptionId === base.authenticityOptionId) && (
                          <MenuItem value={base.authenticityOptionId}>{base.authenticityValue || "Current value"}</MenuItem>
                        )}
                      {authenticityOptions.map((o) => (
                        <MenuItem key={o.authenticityOptionId} value={o.authenticityOptionId}>
                          {o.value}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>

                  {/* Read-only numbers */}
                  <Grid container spacing={1.25}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <StatTile
                        icon={Inventory2OutlinedIcon}
                        label="In stock"
                        color={totalStock > 0 ? "success" : "error"}
                        value={totalStock > 0 ? totalStock : "Out"}
                        hint={activeVariants.length > 0 ? "all variants" : undefined}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <StatTile icon={ShoppingBagOutlinedIcon} label="Sold" value={base.soldCount ?? 0} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <StatTile
                        icon={StarRoundedIcon}
                        label="Rating"
                        color="warning"
                        value={rating > 0 ? rating.toFixed(1) : "—"}
                        hint={rating > 0 ? undefined : "no ratings"}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <StatTile
                        icon={StyleOutlinedIcon}
                        label="Variants"
                        color="secondary"
                        value={variants.length}
                        hint={variants.length > 0 ? `${activeVariants.length} active` : undefined}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Description & translations */}
          <SectionCard
            title="Description & translations"
            icon={TranslateRoundedIcon}
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={translating ? <CircularProgress size={14} /> : <TranslateRoundedIcon />}
                disabled={translating || (!form.name.trim() && !form.description.trim())}
                onClick={handleTranslate}
              >
                Auto-translate from English
              </Button>
            }
          >
            <Grid container spacing={2}>
              {translationColumns.map((col) => {
                const name = col.nameField ? form[col.nameField] : form.name;
                const missing = !name.trim() || (form.description.trim() && !form[col.descField].trim());
                return (
                  <Grid key={col.code} size={{ xs: 12, md: 4 }}>
                    <Stack direction="row" sx={{ alignItems: "center", mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, flexGrow: 1 }}>
                        {col.title}
                      </Typography>
                      {missing ? (
                        <Chip size="small" color="warning" variant="outlined" label="Missing" sx={{ height: 20, fontSize: 11 }} />
                      ) : (
                        <CheckCircleRoundedIcon color="success" sx={{ fontSize: 16 }} />
                      )}
                    </Stack>
                    <Stack spacing={1}>
                      {col.nameField && (
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Name"
                          value={form[col.nameField]}
                          onChange={(e) => set(col.nameField)(e.target.value)}
                          sx={{ ...editSx, "& .MuiInputBase-input": { fontWeight: 700 } }}
                          slotProps={{ htmlInput: { dir: col.dir, lang: col.code } }}
                        />
                      )}
                      <TextField
                        fullWidth
                        multiline
                        minRows={col.nameField ? 4 : 5}
                        maxRows={14}
                        placeholder="Description"
                        value={form[col.descField]}
                        onChange={(e) => set(col.descField)(e.target.value)}
                        sx={editSx}
                        slotProps={{ htmlInput: { dir: col.dir, lang: col.code } }}
                      />
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </SectionCard>

          <Grid container spacing={{ xs: 2, md: 2.5 }}>
            {/* Specifications */}
            <Grid size={{ xs: 12, md: 5 }}>
              <SectionCard title="Specifications" icon={TuneRoundedIcon}>
                {categoryAttributes === null ? (
                  <Stack spacing={1}>
                    <Skeleton variant="rounded" height={38} />
                    <Skeleton variant="rounded" height={38} />
                  </Stack>
                ) : attributesFailed ? (
                  <>
                    <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                      Couldn't load the specification list - showing saved values.
                    </Alert>
                    {(base.attributeValues || []).map((av) => (
                      <InfoRow key={av.attributeId} label={av.attributeName}>
                        {av.optionValue ?? av.textValue}
                        {av.unit ? ` ${av.unit}` : ""}
                      </InfoRow>
                    ))}
                  </>
                ) : categoryAttributes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    This category has no specifications.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {categoryAttributes.map((attr) => {
                      const value = form.attributeValues[attr.attributeId];
                      const label = `${attr.attributeName}${attr.unit ? ` (${attr.unit})` : ""}`;
                      return (
                        <Box key={attr.attributeId}>
                          <FieldLabel hint={attr.isRequired ? "required" : undefined}>{label}</FieldLabel>
                          {attr.inputType === ATTRIBUTE_INPUT_DROPDOWN ? (
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={value?.optionId ?? ""}
                              onChange={(e) =>
                                setAttr(attr.attributeId, { optionId: e.target.value === "" ? null : Number(e.target.value), textValue: "" })
                              }
                              error={attr.isRequired && value?.optionId == null}
                              sx={editSx}
                              slotProps={{ select: { displayEmpty: true } }}
                            >
                              <MenuItem value="">
                                <Typography component="span" color="text.secondary">
                                  Not set
                                </Typography>
                              </MenuItem>
                              {(attr.options || []).map((o) => (
                                <MenuItem key={o.optionId} value={o.optionId}>
                                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                    {o.colorHex && (
                                      <Box
                                        sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: o.colorHex, border: "1px solid", borderColor: "divider" }}
                                      />
                                    )}
                                    <span>{o.value}</span>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              type={attr.inputType === ATTRIBUTE_INPUT_NUMBER ? "number" : "text"}
                              placeholder="Not set"
                              value={value?.textValue ?? ""}
                              onChange={(e) => setAttr(attr.attributeId, { optionId: null, textValue: e.target.value })}
                              error={attr.isRequired && !(value?.textValue || "").trim()}
                              sx={editSx}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </SectionCard>
            </Grid>

            {/* Variants */}
            <Grid size={{ xs: 12, md: 7 }}>
              <SectionCard
                title={`Variants (${variants.length})`}
                icon={StyleOutlinedIcon}
                action={
                  <Button size="small" startIcon={<EditNoteRoundedIcon />} onClick={() => guard(() => onOpenFullEditor(base))}>
                    Manage variants
                  </Button>
                }
              >
                <VariantsList variants={variants} />
              </SectionCard>
            </Grid>
          </Grid>

          {/* Record */}
          <Paper variant="outlined" sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25, borderRadius: 2.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 3 }} sx={{ color: "text.secondary" }}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                <ScheduleRoundedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">Created {formatDate(base.createdAt)}</Typography>
              </Stack>
              <Typography variant="caption">Last updated {base.updatedAt ? formatDate(base.updatedAt) : "never"}</Typography>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                <LocalOfferOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">
                  Category: {base.categoryName || "—"}{" "}
                  <Box
                    component="button"
                    type="button"
                    onClick={() => guard(() => onOpenFullEditor(base))}
                    sx={{ border: 0, p: 0, bgcolor: "transparent", color: "primary.main", cursor: "pointer", font: "inherit", fontWeight: 600 }}
                  >
                    Change
                  </Box>
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      {/* Save bar */}
      <DialogActions
        disableSpacing
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.25,
          gap: 1,
          flexWrap: "wrap",
          bgcolor: dirty ? (theme) => alpha(theme.palette.warning.main, 0.08) : "background.paper",
          borderTop: "1px solid",
          borderColor: dirty ? "warning.light" : "divider",
          transition: "background-color .2s",
        }}
      >
        <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
          {shownError ? (
            <Alert severity="error" onClose={error ? () => setError("") : undefined} sx={{ py: 0, borderRadius: 2 }}>
              {shownError}
            </Alert>
          ) : dirty ? (
            <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.dark" }}>
              ● Unsaved changes{" "}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "inline" } }}>
                · Ctrl+S to save
              </Typography>
            </Typography>
          ) : savedFlash ? (
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", color: "success.main" }}>
              <CheckCircleRoundedIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                All changes saved
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Click any field to edit it.
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1, marginInlineStart: "auto" }}>
          {dirty ? (
            <Button color="inherit" disabled={busy} onClick={() => setForm(formFromProduct(base))}>
              Discard
            </Button>
          ) : (
            <Button color="inherit" disabled={busy} onClick={onClose}>
              Close
            </Button>
          )}
          {canReject && (
            <Button variant="outlined" color="error" startIcon={<CancelRoundedIcon />} disabled={busy} onClick={() => guard(onReject)}>
              Reject
            </Button>
          )}
          {canApprove && (
            <Button variant="outlined" color="success" startIcon={<CheckCircleRoundedIcon />} disabled={busy} onClick={handleApprove}>
              {dirty ? "Save & approve" : "Approve"}
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            disabled={!dirty || busy}
            onClick={save}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
            sx={{ px: 2.5 }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Stack>
      </DialogActions>

      <Dialog open={Boolean(confirmDiscard)} onClose={() => setConfirmDiscard(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You changed this product but didn't save. Your edits will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(null)}>Keep editing</Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={() => {
              const action = confirmDiscard;
              setConfirmDiscard(null);
              action();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
